import { Injectable } from '@angular/core';
import { Observable, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { DocItems, Document } from 'src/app/shared/document';
import { ProductInfo } from 'src/app/shared/product';
import { ProductApiService } from './product-api.service';
import { ClientApiService } from './client-api.service';
import { GetglobalsettingsService } from './getglobalsettings.service';
import { round1, round2 } from 'src/app/shared/round';
import { AgentCustomerTier } from 'src/app/shared/agent-admin';

/**
 * Which of `ProductInfo`'s pre-computed discount prices a customer-level tier sells at.
 * A tier absent from this map (there are none as of 2026-09-15, but a future tier value
 * added without updating this map falls through safely) prices at catalogue retail --
 * see `priceRow()`'s `discountField` fallback. Mapping decided by the owner (see
 * `PRODUCT-INTENT.md`'s "Customer level, application-wide" open thread):
 * `key_partner` always gets the maximum discount from day one; `key_account` is the
 * more complex case (one dedicated manager, discount negotiated per-relationship, not
 * automatically maximal); `standard`/`new`/`edge` price identically at the base tier.
 */
const DISCOUNT_FIELD_BY_TIER: Partial<Record<AgentCustomerTier, keyof Pick<ProductInfo, 'BASEDISCPRICE' | 'QTYDISCPRICE' | 'DIALDISCPRICE'>>> = {
  key_partner: 'DIALDISCPRICE',
  key_account: 'QTYDISCPRICE',
  standard: 'BASEDISCPRICE',
  new: 'BASEDISCPRICE',
  edge: 'BASEDISCPRICE',
};

/**
 * Same tier-to-rung mapping as {@link DISCOUNT_FIELD_BY_TIER}, expressed as the raw
 * *percentage* fields (`PRODBASEDISC`/`PRODQTYDISC`/`PRODDIALDISC`) instead of the
 * pre-computed price fields. `priceRow()` below wants a price directly; `edit-document-content`'s
 * `onProductSelection()` (2026-09-21) prices a row by setting `di.DISCOUNT` and multiplying
 * `PRODPRICE * (100 - DISCOUNT) / 100` itself, so it needs the percentage, not a price. Kept
 * as its own explicit map rather than derived from `DISCOUNT_FIELD_BY_TIER` at call time
 * (e.g. `100 * (1 - discountPrice / retailPrice)`) to avoid a lossy round-trip through
 * floating-point division for a value that is already an exact column on the product row.
 */
export const DISCOUNT_PERCENT_FIELD_BY_TIER: Partial<Record<AgentCustomerTier, keyof Pick<ProductInfo, 'PRODBASEDISC' | 'PRODQTYDISC' | 'PRODDIALDISC'>>> = {
  key_partner: 'PRODDIALDISC',
  key_account: 'PRODQTYDISC',
  standard: 'PRODBASEDISC',
  new: 'PRODBASEDISC',
  edge: 'PRODBASEDISC',
};

/**
 * The discount percentage (0-100) a customer's tier entitles them to on one product, per
 * {@link DISCOUNT_PERCENT_FIELD_BY_TIER}. No tier, an unmapped tier, or a missing/null field
 * on `info` all return `0` (catalogue retail) -- the same "fail toward no discount, never
 * toward an unearned one" fallback `priceRow()` already uses.
 */
export function getTierDiscountPercent(
  tier: AgentCustomerTier | null | undefined,
  info: Pick<ProductInfo, 'PRODBASEDISC' | 'PRODQTYDISC' | 'PRODDIALDISC'>,
): number {
  const percentField = tier ? DISCOUNT_PERCENT_FIELD_BY_TIER[tier] : undefined;
  return (percentField ? info?.[percentField] : null) ?? 0;
}

/**
 * Retail pricing for document rows that do not have a price yet.
 *
 * Extracted into a service because two unrelated places need the same answer: the products
 * panel, when a manager picks a product by hand, and `create-new-document`, when the AI
 * Assist tab seeds rows from an email. The first version priced only the panel's own rows,
 * so an AI-proposed product stayed at 0 while an identical one added by hand came back
 * priced -- the same product, two prices, decided by how it got into the document.
 *
 * `getProdPriceInfoById` is the source, matching `edit-document-content`'s own
 * `pullDiRowPriceAndStockObservable()`, which documents it as returning the
 * "retail/discount fields" and passes the same three stock ids from global settings.
 * `ProductInfo` annotates `PRODPRICE` as coming from this call and *not* from the
 * stock-info one -- using the latter is what produced zero prices to begin with.
 *
 * The response also carries `PRODBASEDISC`/`BASEDISCPRICE`/`QTYDISCPRICE`/`DIALDISCPRICE`.
 * Customer-level pricing (2026-09-15) picks one of the three discounted prices via
 * `DISCOUNT_FIELD_BY_TIER`, keyed off `doc.DOCCLIENTSID`'s `AgentCustomerProfiles.tier`
 * (`ClientApiService.getDiscountTier()`) -- falling back to catalogue retail (`PRODPRICE`)
 * when no tier is recorded, same as before this chunk.
 *
 * **Fixed 2026-09-20 -- was keyed off `doc.DOCMANCLIID` (the WRONG field).**
 * `DOCMANCLIID` is *our own* issuing/managing entity, not the customer --
 * confirmed via `new-document-dlg-companies.component.ts`, where the buyer
 * selector binds `[selectedID]="newDocument.DOCCLIENTSID"` and `DOCMANCLIID`
 * never appears on that (customer-facing) side of the dialog at all; see also
 * `SQL-Database-migration/CLAUDE.md`'s "Multi-legal-entity depots" section,
 * whose ground-truth document showed `DOCMANCLIID` and `DOCCLIENTSID` as two
 * independent fields, `DOCMANCLIID` on OUR side. `AgentCustomerProfiles.tier`
 * is only ever set against a real customer `CLIID`, so looking it up by
 * `DOCMANCLIID` (almost always one of PROXIS/DISTI's own `Clients` rows,
 * which have no tier) silently returned `tier: null` on effectively every
 * document -- the tier-based pricing feature never actually applied a
 * discount since it shipped. `DOCPCOCLIID` (the customer's specific Legal
 * Entity, a child of `DOCCLIENTSID`) would resolve identically once past the
 * webapi's Main Company resolution (`GET /:id/discount-tier`, see
 * `webyto_api-express4-TS-20250331/routes/Clients.ts`), so either field
 * works -- `DOCCLIENTSID` is used here since it is always populated the
 * moment a customer is picked, before `DOCPCOCLIID` is ever touched.
 */
@Injectable({ providedIn: 'root' })
export class DocItemPricingService {

  constructor(
    private prodApi: ProductApiService,
    private cliApi: ClientApiService,
    private globalsettingsservice: GetglobalsettingsService,
  ) { }

  /**
   * Fills `DOCITEMPRICE`, `PRODVAT`, `DOCITEMSUM` and `prodInfo` on one row, in place.
   *
   * Never errors: a lookup failure resolves with the row unchanged at price 0. The row is
   * already in the document by this point and its price cell is hand-editable, so failing
   * loudly would cost the manager the line for no gain. Both empty outcomes are logged
   * distinctly -- no row returned, versus a row carrying no price -- so a zero is always
   * explainable after the fact.
   */
  priceRow(row: DocItems, doc: Document): Observable<DocItems> {
    if (!row?.PRODID || !doc) { return of(row); }

    const gs = this.globalsettingsservice.getGlobalSettings();

    return forkJoin({
      priceInfo: this.prodApi.getProdPriceInfoById(
        row.PRODID,
        doc.DOCMANCLIID,
        doc.DOCCURRID,
        gs.getMainStock(),
        gs.getImpStock(),
        gs.getAuxStock(),
        new Date()
      ),
      // Falls back to "no tier" on any failure -- a discount-lookup error must never
      // block pricing the row at all, it should just leave it at catalogue retail.
      // DOCCLIENTSID, not DOCMANCLIID -- see this service's own header comment.
      discountTier: doc.DOCCLIENTSID
        ? this.cliApi.getDiscountTier(doc.DOCCLIENTSID).pipe(catchError(() => of({ tier: null as AgentCustomerTier | null })))
        : of({ tier: null as AgentCustomerTier | null }),
    }).pipe(
      map(({ priceInfo: data, discountTier }) => {
        if (!data || data.length === 0) {
          console.warn(`*** DocItemPricingService: no price info for PRODID ${row.PRODID}`);
          return row;
        }

        const info = data[0];
        row.prodInfo = info;
        row.PRODVAT = row.PRODVAT ?? gs.vatValue;

        if (info.PRODPRICE === null || info.PRODPRICE === undefined) {
          console.warn(`*** DocItemPricingService: PRODID ${row.PRODID} has no PRODPRICE`);
          return row;
        }

        // `|| 1` guards the window before the currency selector has emitted a rate --
        // without it a freshly-seeded document prices everything at 0 and looks broken.
        const rate = doc.DOCRATEVAL || 1;
        const kVat = (100.0 + (row.PRODVAT ?? 0)) / 100.0;

        // Customer-level discount (2026-09-15): `discountTier.tier` selects which of
        // `info.BASEDISCPRICE`/`QTYDISCPRICE`/`DIALDISCPRICE` this customer sells at --
        // null/unmapped tier, or a null discount field on this specific product, both
        // fall back to `info.PRODPRICE` (catalogue retail, 0% discount), same as before
        // this chunk. See `DISCOUNT_FIELD_BY_TIER`'s own doc comment for the mapping.
        const discountField = discountTier.tier ? DISCOUNT_FIELD_BY_TIER[discountTier.tier] : undefined;
        const sellPrice = (discountField ? info[discountField] : null) ?? info.PRODPRICE;

        // PRODPRICE is the catalogue retail price in the *foreign* currency: dividing the
        // document-currency price back by the rate is exactly what
        // new-document-dlg-process-invoice does when it derives a child document's rows
        // (`DOCITEMVPRICE = DOCITEMPRICE / DOCRATEVAL`), and edit-document-content reads
        // PRODPRICE straight into DOCITEMRETVPRICE. Filling only DOCITEMPRICE left the
        // "Цена $"/"Сумма $" columns at zero on the saved document.
        row.DOCITEMRETVPRICE = round2(info.PRODPRICE);
        row.DOCITEMRETPRICE = round2(row.DOCITEMRETVPRICE * rate);

        // The document-currency price is derived from the *rounded* foreign one, not from
        // raw PRODPRICE. edit-document-content flags a row when
        // `round2(DOCITEMVPRICE * DOCRATEVAL) != round2(DOCITEMPRICE)` ("Валютная цена не
        // соответствует расчетной"), and deriving the two independently fails that by a
        // cent: a catalogue price of 669.9966 rounds to 670.00 while 669.9966 * 47.2
        // rounds to 31 623.84, where 670.00 * 47.2 is 31 624.00. Rounding once, then
        // converting, keeps the pair reconcilable by exactly the check that reads it.
        row.DOCITEMVPRICE = round2(sellPrice);
        row.DOCITEMPRICE = round2(row.DOCITEMVPRICE * rate);
        row.DOCITEMPRICEWOVAT = round1(row.DOCITEMPRICE / kVat);

        // The row's own currency and rate must match the document's, or the saved
        // document flags "Курс не соответствует установленному в документе"
        // (`DOCITEMCRATE != DOCRATEVAL`). Set here rather than trusted from whoever built
        // the row: this service is what decided the figures, so it owns the rate they
        // were computed at.
        row.DOCITEMCURRID = doc.DOCCURRID;
        row.DOCITEMCRATE = rate;

        // Same formula `recalcRowFromPrice()` below uses, so a discounted row and a
        // hand-edited one agree on what "discount" means. 0% (sellPrice === retail) when
        // no tier is recorded, same visible result as before this chunk.
        row.DISCOUNT = row.DOCITEMRETVPRICE ? round2((1 - (row.DOCITEMVPRICE / row.DOCITEMRETVPRICE)) * 100) : 0;

        this.recalcRowSums(row);
        return row;
      }),
      catchError(err => {
        console.error(`*** DocItemPricingService: price lookup failed for PRODID ${row.PRODID}:`, err);
        return of(row);
      }),
    );
  }

  /**
   * Re-derives everything that follows from a hand-edited quantity or price: the foreign
   * currency price, the discount against catalogue retail, and both totals.
   *
   * Same derivation `edit-document-content` uses on a saved document, so a row edited in
   * the products panel and the same row edited after creation agree. Without it, typing a
   * lower price left "Цена $" and the discount showing the untouched catalogue figures.
   */
  recalcRowFromPrice(row: DocItems, doc: Document): void {
    const rate = doc?.DOCRATEVAL || 1;
    const kVat = (100.0 + (row.PRODVAT ?? 0)) / 100.0;

    // Converting back and forth re-rounds, so the typed figure is snapped to the nearest
    // value the foreign price can actually reproduce -- at most a cent, and the
    // alternative is a saved document permanently flagged "Валютная цена не соответствует
    // расчетной" for a rounding artefact nobody can correct by typing.
    row.DOCITEMVPRICE = round2((row.DOCITEMPRICE ?? 0) / rate);
    row.DOCITEMPRICE = round2(row.DOCITEMVPRICE * rate);
    row.DOCITEMPRICEWOVAT = round1(row.DOCITEMPRICE / kVat);

    row.DOCITEMCURRID = doc?.DOCCURRID ?? row.DOCITEMCURRID;
    row.DOCITEMCRATE = rate;

    // Only meaningful once a catalogue retail price is known; left alone otherwise rather
    // than divided by zero.
    if (row.DOCITEMRETVPRICE) {
      row.DISCOUNT = round2((1 - (row.DOCITEMVPRICE / row.DOCITEMRETVPRICE)) * 100);
    }

    this.recalcRowSums(row);
  }

  /** Keeps both currency totals in step with the row's price and quantity. */
  recalcRowSums(row: DocItems): void {
    const qty = row.DOCITEMQTY ?? 0;
    row.DOCITEMSUM = round2(qty * (row.DOCITEMPRICE ?? 0));
    row.DOCITEMVSUM = round2(qty * (row.DOCITEMVPRICE ?? 0));
  }

  /**
   * Prices every row of `doc` that still has no price, one at a time.
   *
   * Sequential rather than parallel on purpose: an email can produce a dozen lines, and
   * this is a background convenience filling in a form the manager is already reading --
   * not worth a burst of simultaneous requests. Rows that already carry a price are left
   * exactly as they are, so a hand-corrected figure is never overwritten.
   */
  priceMissingRows(doc: Document): Observable<DocItems> {
    const pending = (doc?.items ?? []).filter(row => row.PRODID && !row.DOCITEMPRICE);
    return from(pending).pipe(concatMap(row => this.priceRow(row, doc)));
  }
}
